import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { supabase } from '../../lib/supabase';
import { useNytaChat } from '../../hooks/useNytaChat';
import { useNytaConversations } from '../../hooks/useNytaConversations';
import { useNytaModal } from '../../hooks/useNytaModal';
import { useNytaModalStore } from '../../stores/nytaModalStore';
import { useArtistCapabilities } from '../../hooks/useArtistCapabilities';
import { useEntitlements } from '../../hooks/useEntitlements';
import { useAppSelector } from '../../store/store';
import { NYTA_SUGGESTIONS, PAYWALL_DISABLED } from '../../constants/maestra';
import { AVISO_DO_TITULAR, LOCKED_FEATURE_CONFIG } from '../../constants/bloqueios';
import { CONVITE_DO_CAMPO, RESSALVA_DA_NYTA, saudacaoDaNyta, saudacaoDoPerfil } from '../../constants/nytaChat';
import { aindaNoLimite, cronometro, liberacaoDaNyta, quandoVolta } from '../../nucleo/limiteDaNyta';
import { acoesDoHistorico, translateToolName } from '../../nucleo/acoesDaNyta';
import { sanitizeNytaContent } from '../../utils/sanitizeNytaContent';
import { dataDaConversa } from '../../nucleo/dataDaConversa';
import type { AnexoDaNyta } from '../../nucleo/graficoDaConversa';
import type { PendingToolCall } from '../../store/slices/nytaChat';

import { camposDa, moduloDa } from './cartaoDaAcao';
import { useCaminhoDoPro } from '../dinheiro/caminhoDoPro';
import type { Ir } from '../porta';

// A CONVERSA COM A NYTA, sem tela: a mesma para a página (/artists/:id/nyta) e para o painel
// flutuante do desktop. O chat é o do núcleo (`useNytaChat`: o streaming, o limite, confirmar e
// cancelar a ação); aqui fica o que a tela precisa, já decidido.
//
// ⚠️ A NYTA SEGUE QUEM PAGA O PERFIL (`useNytaConsultora`, a mesma regra da edge `nyta-chat`): o
// convidado de um titular que assina usa, com o próprio limite; o dono num perfil fora da cota,
// não. O convidado nunca vê venda (o PRO dele não destravaria nada): vê o aviso do titular.
//
// ⚠️ "NOVA CONVERSA" ABRE UMA CONVERSA NOVA DE VERDADE. Sem o id, o servidor devolve a conversa
// mexida por último (`getOrCreateConversation`), e a pergunta "nova" caía na anterior, com o
// contexto dela. A linha nasce aqui, com a sessão de quem pergunta (a mesma RLS que o servidor
// usa), só na PRIMEIRA mensagem: criar no clique encheria a lista de conversas vazias.
//
// Desde 31/10/2026 mora no núcleo, e a web e o app desenham a mesma conversa (docs/PLANO-APP-NATIVO.md).
//
// ⚠️ O CARTÃO DA AÇÃO NÃO SOME DEPOIS DE CONFIRMAR. O desenho antigo só mostrava as pendentes, e a
// ação confirmada desaparecia até recarregar. Aqui ela fica, com o estado dela.

export type EstadoDaAcao = 'pendente' | 'executando' | 'confirmada' | 'feita' | 'cancelada' | 'falhou';

export interface AcaoNaConversa {
  id: string;
  /** O nome da ferramenta (a explicação sabe o que cada uma faz e como se desfaz). */
  ferramenta: string;
  /** O módulo onde a ação grava, e o caminho para abri-lo. */
  modulo: string | null;
  abrirModulo: (() => void) | null;
  /** Apagar não se desfaz. */
  apaga: boolean;
  /** "Criar evento", "Arquivar estratégia": o nome da ferramenta em português (núcleo). */
  titulo: string;
  /** Os campos que a pessoa está autorizando, sem os ids. */
  campos: { rotulo: string; valor: string; lista?: string[] }[];
  estado: EstadoDaAcao;
  /** Só a pendente (e a que falhou, para tentar de novo) se decide. */
  confirmar?: () => void;
  cancelar?: () => void;
}

export type ItemDaConversa =
  | { tipo: 'pessoa'; id: string; texto: string }
  | { tipo: 'nyta'; id: string; texto: string; anexos?: Record<string, AnexoDaNyta>; acoes: AcaoNaConversa[]; escrevendo: boolean; leituras: string[] | null }
  | { tipo: 'semTexto'; id: string }
  | { tipo: 'falhou'; id: string; tentar: (() => void) | null }
  | { tipo: 'acao'; id: string; acao: AcaoNaConversa };

const ESTADO_AO_VIVO: Record<PendingToolCall['status'], EstadoDaAcao> = {
  pending: 'pendente', executing: 'executando', confirmed: 'confirmada', done: 'feita', cancelled: 'cancelada', error: 'falhou',
};
const ESTADO_DO_HISTORICO: Record<'executing' | 'done' | 'error', EstadoDaAcao> = {
  executing: 'executando', done: 'feita', error: 'falhou',
};

/** O aviso de leitura do servidor, e a fonte que ele vira na explicação da resposta. */
const FONTE_DO_STATUS: [RegExp, string][] = [
  [/internet/i, 'internet'],
  [/chartmetric/i, 'chartmetric'],
  [/relat[oó]rio/i, 'relatorio'],
  // Montar o gráfico não é fonte: a fonte é a do gráfico (`fonte` do anexo).
];

/**
 * O que cada superfície dá à conversa:
 *   · `ir` (`telas/porta`);
 *   · `artistId`: o perfil da rota, quando a superfície o sabe (o app); sem ele, o do contexto da
 *     Nyta (a web, que o põe pela casca);
 *   · `pedido`: a pergunta que chega pronta na página (`?prompt=` na web, `?pergunta=` no app) e o
 *     que a apaga do endereço depois de enviada;
 *   · `telaPequena`: se abrir um módulo a partir do painel flutuante deve fechá-lo (a web, abaixo
 *     de 672 px).
 */
export interface PortasDaConversa {
  ir: Ir;
  artistId?: string | null;
  pedido?: { texto: string | null; consumir: () => void };
  telaPequena?: () => boolean;
}

export const useConversaDaNyta = (onde: 'pagina' | 'flutuante', { ir: navigate, artistId: artistIdDaTela, pedido, telaPequena }: PortasDaConversa) => {
  const { isOpen: painelAberto, close: fecharPainel } = useNytaModal();
  const artistIdDoContexto = useNytaModalStore((s) => s.moduleContext.artistId);
  const artistId = artistIdDaTela ?? artistIdDoContexto;
  const pendingPrompt = useNytaModalStore((s) => s.pendingPrompt);
  const clearPendingPrompt = useNytaModalStore((s) => s.clearPendingPrompt);
  const artist = useAppSelector((s) => (artistId ? s.artists.items.find((a) => a.id === artistId) : undefined));
  // O caminho para o PRO (grátis, pagamento pendente, perfil fora da cota, em atraso).
  const caminho = useCaminhoDoPro(artist, navigate);
  const usuario = useAppSelector((s) => s.auth.user);
  const quemEntrou = usuario?.user_metadata as Record<string, unknown> | undefined;
  const nome = (quemEntrou?.full_name || quemEntrou?.name) as string | undefined;
  const entitlements = useEntitlements();
  const capacidades = useArtistCapabilities(artist);

  const { conversations, refresh, rename, remove } = useNytaConversations(onde === 'pagina' ? artistId ?? undefined : undefined);
  const aoGravarNaConversa = useCallback(() => { if (onde === 'pagina') void refresh(); }, [onde, refresh]);
  const chat = useNytaChat(onde === 'pagina' ? 'route' : 'modal', aoGravarNaConversa);
  const {
    messages, isStreaming, pendingToolCalls, rateLimitInfo, loadingHistory, hasMoreHistory, error, unavailableModules,
    conversationId, selectConversation, startNewConversation, sendMessage, stopStreaming, confirmTool, cancelTool,
    loadOlderMessages, clearConversation, dismissError,
  } = chat;
  const statusDaResposta = useAppSelector((s) => s.nytaChat.statusDaResposta);

  // ── O acesso ──
  const temAcesso = artist ? capacidades.useNytaConsultora : (PAYWALL_DISABLED || entitlements.isPro);
  const papel = artist?.role === 'member' ? 'membro' : 'dono';
  const travada = (!PAYWALL_DISABLED && !temAcesso) || error === 'subscription_required';

  // ── A conversa nova de verdade ──
  const emNova = useRef(false);
  const aguardando = useRef<{ id: string; texto: string } | null>(null);
  const [criando, setCriando] = useState(false);

  const novaConversa = useCallback(() => {
    emNova.current = true;
    startNewConversation();
  }, [startNewConversation]);

  const enviar = useCallback(async (texto: string) => {
    const t = texto.trim();
    if (!t) return;
    if (!conversationId && emNova.current && artistId && usuario?.id) {
      setCriando(true);
      const { data, error: erro } = await supabase
        .from('nyta_conversations')
        .insert({ user_id: usuario.id, artist_id: artistId })
        .select('id')
        .single();
      emNova.current = false;
      if (erro || !data) {
        // Sem a linha, segue o caminho antigo: melhor a resposta na conversa anterior do que nenhuma.
        setCriando(false);
        sendMessage(t);
        return;
      }
      aguardando.current = { id: data.id, texto: t };
      await selectConversation(data.id);
      if (onde === 'pagina') void refresh();
      return;
    }
    sendMessage(t);
  }, [conversationId, artistId, usuario?.id, sendMessage, selectConversation, onde, refresh]);

  // A pergunta da conversa recém-criada sai quando a conversa já é a aberta (e vazia).
  useEffect(() => {
    const a = aguardando.current;
    if (!a || conversationId !== a.id || loadingHistory) return;
    aguardando.current = null;
    setCriando(false);
    sendMessage(a.texto);
  }, [conversationId, loadingHistory, sendMessage]);

  // ── A pergunta que chega pronta: ?prompt= na página, e a fila do painel ──
  const promptEnviado = useRef(false);
  useEffect(() => {
    if (onde !== 'pagina') return;
    const prompt = pedido?.texto?.trim();
    if (!prompt || promptEnviado.current || loadingHistory || travada) return;
    promptEnviado.current = true;
    void enviar(prompt);
    pedido?.consumir();
  }, [onde, loadingHistory, pedido, enviar, travada]);

  useEffect(() => {
    // ⚠️ Só depois de o histórico carregar: antes, a carga chegava depois e apagava a pergunta. E
    // sem acesso ela nem é enviada (seria recusada e gastaria uma ida).
    if (onde !== 'flutuante' || !painelAberto || !pendingPrompt || loadingHistory) return;
    if (temAcesso) void enviar(pendingPrompt);
    clearPendingPrompt();
  }, [onde, painelAberto, pendingPrompt, loadingHistory, temAcesso, enviar, clearPendingPrompt]);

  // ── O limite do dia (o relógio anda enquanto o contador está cheio) ──
  const [agora, setAgora] = useState(() => new Date());
  const contadorCheio = !!rateLimitInfo && rateLimitInfo.count >= rateLimitInfo.limit;
  useEffect(() => {
    if (!contadorCheio) return undefined;
    setAgora(new Date());
    const tique = setInterval(() => setAgora(new Date()), 1000);
    return () => clearInterval(tique);
  }, [contadorCheio]);
  const noLimite = aindaNoLimite(rateLimitInfo, agora);
  const liberacao = noLimite && rateLimitInfo ? liberacaoDaNyta(rateLimitInfo.resetAt, agora) : null;

  // ── O que a Nyta foi ler para cada resposta ──
  // O servidor avisa cada leitura (`status`: "Buscando na internet…") e o núcleo descarta o aviso
  // quando o texto começa. Aqui ele vira a fonte da resposta, para a explicação do rótulo de IA
  // dizer de onde ela veio. Só nesta sessão: a conversa que volta do histórico não sabe (`null`).
  const leiturasEmCurso = useRef<string[]>([]);
  const [leiturasDa, setLeiturasDa] = useState<Record<string, string[]>>({});
  const estavaEscrevendo = useRef(false);
  useEffect(() => {
    if (!isStreaming || !statusDaResposta) return;
    const fonte = FONTE_DO_STATUS.find(([re]) => re.test(statusDaResposta))?.[1];
    if (fonte && !leiturasEmCurso.current.includes(fonte)) leiturasEmCurso.current = [...leiturasEmCurso.current, fonte];
  }, [isStreaming, statusDaResposta]);
  useEffect(() => {
    if (isStreaming && !estavaEscrevendo.current) leiturasEmCurso.current = [];
    if (!isStreaming && estavaEscrevendo.current) {
      // No fim, a mensagem já tem o id do servidor: é nele que as leituras ficam.
      const ultimaDaNyta = [...messages].reverse().find((m) => m.role === 'assistant');
      const lidas = leiturasEmCurso.current;
      if (ultimaDaNyta) setLeiturasDa((m) => ({ ...m, [ultimaDaNyta.id]: lidas }));
      leiturasEmCurso.current = [];
    }
    estavaEscrevendo.current = isStreaming;
  }, [isStreaming, messages]);

  // ── O fio ──
  const ondeGrava = (nome: string) => {
    const m = moduloDa(nome);
    return {
      modulo: m ? m[1] : null,
      abrirModulo: m && artistId ? () => { if (onde === 'flutuante' && telaPequena?.()) fecharPainel(); navigate(`/artists/${artistId}/${m[2]}`); } : null,
      apaga: /delete|remove/.test(nome),
    };
  };
  const idsAoVivo = pendingToolCalls.map((tc) => tc.toolCallId);
  const fio = useMemo<ItemDaConversa[]>(() => {
    const itens: ItemDaConversa[] = [];
    const itemDaAcao = (tc: PendingToolCall): ItemDaConversa => {
      const podeDecidir = tc.status === 'pending' || tc.status === 'error';
      return {
        tipo: 'acao',
        id: tc.toolCallId,
        acao: {
          id: tc.toolCallId,
          ferramenta: tc.name,
          titulo: translateToolName(tc.name),
          campos: camposDa(tc.arguments),
          estado: ESTADO_AO_VIVO[tc.status],
          ...ondeGrava(tc.name),
          confirmar: podeDecidir ? () => { void confirmTool(tc.toolCallId); } : undefined,
          cancelar: podeDecidir ? () => { void cancelTool(tc.toolCallId); } : undefined,
        },
      };
    };
    // Cada cartão ao vivo entra logo depois da resposta que o pediu, mesmo que essa resposta não
    // tenha virado item (a Nyta que só chama a ferramenta não escreve texto nenhum).
    const porMensagem = new Map<string, PendingToolCall[]>();
    const semMensagem: PendingToolCall[] = [];
    pendingToolCalls.forEach((tc) => {
      if (!tc.messageId) { semMensagem.push(tc); return; }
      const lista = porMensagem.get(tc.messageId);
      if (lista) lista.push(tc); else porMensagem.set(tc.messageId, [tc]);
    });
    // O resumo do resultado de cada ação (é ele que distingue "cancelada" de "falhou").
    const resumos = new Map<string, string>();
    messages.forEach((m) => {
      const r = m.toolResults as unknown;
      (Array.isArray(r) ? r : r ? [r] : []).forEach((x: { tool_call_id?: string; summary?: string }) => { if (x.tool_call_id) resumos.set(x.tool_call_id, x.summary ?? ''); });
    });
    const desenhar = (m: typeof messages[number], i: number) => {
      // No limite, a mensagem que não passou não aparece: o aviso do limite já conta a história.
      if (noLimite && m.status === 'error') return;
      if (m.role === 'tool') return;
      if (m.role === 'user') { itens.push({ tipo: 'pessoa', id: m.id, texto: m.content || '' }); return; }
      const texto = sanitizeNytaContent(m.content);
      const acoes = acoesDoHistorico(m, messages, idsAoVivo).map((a) => ({
        id: a.toolCallId, ferramenta: a.name, titulo: translateToolName(a.name), campos: camposDa(a.arguments),
        // O núcleo lê todo `success: false` como falha; o cancelamento também grava assim, e o resumo diz.
        estado: a.status === 'error' && /cancelad/i.test(resumos.get(a.toolCallId) ?? '') ? 'cancelada' as const : ESTADO_DO_HISTORICO[a.status],
        ...ondeGrava(a.name),
      }));
      if (m.status === 'error' && !texto) {
        // Tentar de novo é mandar de novo a pergunta que ficou sem resposta (só na última falha).
        const pergunta = [...messages.slice(0, i)].reverse().find((x) => x.role === 'user')?.content;
        itens.push({ tipo: 'falhou', id: m.id, tentar: i === messages.length - 1 && pergunta ? () => { void enviar(pergunta); } : null });
        return;
      }
      if (!texto && !acoes.length) {
        // Tinha texto e a limpeza o zerou (o modelo respondeu só com markup): diz, em vez de sumir.
        if (!m.toolCalls?.length && m.content?.trim() && m.status !== 'sending') itens.push({ tipo: 'semTexto', id: m.id });
        return;
      }
      const escrevendo = m.status === 'sending' && i === messages.length - 1;
      itens.push({ tipo: 'nyta', id: m.id, texto, anexos: m.anexos, acoes, escrevendo, leituras: escrevendo ? [...leiturasEmCurso.current] : leiturasDa[m.id] ?? null });
    };
    messages.forEach((m, i) => {
      desenhar(m, i);
      (porMensagem.get(m.id) ?? []).forEach((tc) => { itens.push(itemDaAcao(tc)); });
    });
    // As que não sabem de que resposta vieram ficam no fim, como antes.
    semMensagem.forEach((tc) => { itens.push(itemDaAcao(tc)); });
    return itens;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, pendingToolCalls, noLimite, confirmTool, cancelTool, artistId, statusDaResposta, enviar, leiturasDa]);

  // A espera: antes do primeiro texto, e depois dele quando a Nyta saiu para uma leitura.
  const ultima = messages[messages.length - 1];
  const pensando = isStreaming && (!!statusDaResposta || !ultima || ultima.role !== 'assistant' || !ultima.content)
    ? { texto: statusDaResposta || 'Pensando…' }
    : null;
  // Parar só depois que o servidor já gravou a pergunta: abortar antes derrubava a pergunta. O
  // primeiro texto OU o primeiro `status` ("Buscando na internet…") vêm depois da gravação, então a
  // espera mais longa, a das leituras, já pode ser interrompida.
  const respostaComecou = messages.some((m) => m.role === 'assistant' && m.status === 'sending' && !!m.content) || (isStreaming && !!statusDaResposta);
  const temAcaoPendente = pendingToolCalls.some((tc) => tc.status === 'pending');

  const falhaNoFio = messages.some((m) => m.status === 'error');
  const aviso = error && error !== 'subscription_required' && !noLimite && !falhaNoFio ? error : null;

  const conversas = conversations.map((c) => ({
    id: c.id,
    titulo: c.title || 'Nova conversa',
    quando: dataDaConversa(c.updatedAt),
    aberta: c.id === conversationId,
    abrir: () => { emNova.current = false; void selectConversation(c.id); },
    renomear: (titulo: string) => rename(c.id, titulo),
    excluir: async () => {
      const ok = await remove(c.id);
      // Apagar a aberta deixaria a tela mostrando mensagens que não existem mais.
      if (ok && c.id === conversationId) novaConversa();
      return ok;
    },
  }));

  return {
    artistId,
    nomeDoPerfil: artist?.name ?? null,
    travada: travada ? {
      papel,
      // O convidado não pode conversar: o aviso diz isso, e não o genérico de "somente leitura".
      ...(papel === 'membro'
        ? { titulo: 'A Nyta está indisponível neste perfil', texto: `O titular deste perfil está sem assinatura ativa, e a Nyta acompanha a assinatura de quem paga o perfil. Você continua vendo os módulos.`, apoio: AVISO_DO_TITULAR.apoio.replace('a edição libera', 'a Nyta volta'), beneficios: [] as string[] }
        : { titulo: 'A Nyta está no Maestra PRO', texto: 'A IA da Maestra, dentro do seu perfil: responde com os seus dados e faz o trabalho por você, sempre com a sua confirmação.', apoio: null, beneficios: [...LOCKED_FEATURE_CONFIG.nyta.benefits] }),
      rotulo: caminho.rotulo('Assinar Maestra PRO'),
      assinar: papel === 'dono' ? () => { if (onde === 'flutuante') fecharPainel(); caminho.ir(); } : null,
    } : null,
    saudacao: onde === 'pagina' ? saudacaoDaNyta(nome) : saudacaoDoPerfil(artist?.name, nome),
    sugestoes: NYTA_SUGGESTIONS,
    convite: CONVITE_DO_CAMPO,
    ressalva: RESSALVA_DA_NYTA,
    vazia: !messages.length && !loadingHistory && !criando,
    carregandoHistorico: loadingHistory,
    temMaisAntigas: hasMoreHistory,
    carregarAntigas: loadOlderMessages,
    fio,
    pensando,
    escrevendo: isStreaming,
    parar: respostaComecou ? stopStreaming : null,
    podeEscrever: !isStreaming && !temAcaoPendente && !noLimite && !criando,
    esperandoDecisao: temAcaoPendente,
    enviar,
    limite: liberacao && rateLimitInfo ? {
      usadas: rateLimitInfo.limit,
      volta: quandoVolta(liberacao, agora),
      cronometro: cronometro(liberacao, agora),
    } : null,
    uso: rateLimitInfo ? { usadas: rateLimitInfo.count, limite: rateLimitInfo.limit } : null,
    aviso,
    dispensarAviso: dismissError,
    modulosFora: unavailableModules.length
      ? `${unavailableModules.length === 1
        ? `O módulo "${unavailableModules[0]}" está temporariamente indisponível.`
        : `Os módulos ${unavailableModules.map((m) => `"${m}"`).join(', ')} estão temporariamente indisponíveis.`} A Nyta responde com os dados dos demais módulos.`
      : null,
    conversas,
    conversaAberta: conversationId,
    novaConversa,
    // Limpar só apaga a conversa aberta (a antiga sem id caía num `maybeSingle` com várias linhas).
    limpar: conversationId ? () => { void clearConversation(); } : null,
    telaCheia: artistId ? () => navigate(`/artists/${artistId}/nyta`) : null,
    voltarAoPerfil: artistId ? () => navigate(`/artists/${artistId}`) : null,
    painel: { aberto: painelAberto, fechar: fecharPainel },
  };
};

export type DadosDaConversa = ReturnType<typeof useConversaDaNyta>;
